'use client';

import React from 'react';

import { Box, BoxProps } from './box';

export interface SkeletonProps extends BoxProps {
  /**
   * Width of the skeleton
   */
  width?: string | number;
  /**
   * Height of the skeleton
   */
  height?: string | number;
  /**
   * Whether to animate the skeleton
   */
  animation?: 'pulse' | 'wave' | 'none';
  /**
   * Variant of the skeleton
   */
  variant?: 'text' | 'circular' | 'rectangular';
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ variant = 'rectangular', width, height, animation = 'pulse', ...props }, ref) => {
    // Map variants to styles
    const variantStyles = {
      text: { height: height || '16px', borderRadius: '3px' },
      circular: { borderRadius: '9999px' },
      rectangular: { borderRadius: '6px' },
    };

    // Animation styles
    const animationStyles =
      animation !== 'none'
        ? {
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 },
            },
            '@keyframes shimmer': {
              '0%': { backgroundPosition: '-200% 0' },
              '100%': { backgroundPosition: '200% 0' },
            },
            animation:
              animation === 'pulse'
                ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                : 'shimmer 1.5s infinite',
            ...(animation === 'wave'
              ? {
                  background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
                  backgroundSize: '200% 100%',
                }
              : {}),
          }
        : {};

    const { className, ..._htmlProps } = props;

    return (
      <div
        ref={ref}
        className={className}
        style={{
          width,
          height,
          backgroundColor: animation !== 'wave' ? '#E5E7EB' : undefined,
          borderRadius: variantStyles[variant as keyof typeof variantStyles].borderRadius,
          ...animationStyles,
        }}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export interface SkeletonTextProps extends BoxProps {
  /**
   * Number of lines to show
   */
  lineClamp?: number;
  /**
   * Gap between lines
   */
  spacing?: string | number;
  /**
   * Width of each line (can be an array for different widths)
   */
  lineWidths?: string[] | number[];
}

export const SkeletonText = React.forwardRef<HTMLDivElement, SkeletonTextProps>(
  ({ lineClamp = 3, spacing = '8px', lineWidths, ...props }, ref) => {
    const lines = Array.from({ length: lineClamp }, (_, i) => i);

    return (
      <Box ref={ref} {...props}>
        {lines.map((index) => (
          <Skeleton
            key={index}
            variant="text"
            width={lineWidths?.[index] || (index === lines.length - 1 ? '80%' : '100%')}
            marginBottom={index < lines.length - 1 ? spacing : undefined}
          />
        ))}
      </Box>
    );
  }
);

SkeletonText.displayName = 'SkeletonText';

export default Skeleton;
